import { actionCreator, ActionReturnType, Loading, loadingNotStarted, loadingIsLoading, loadingFinished, loadingFailed } from "@itk/typescript-utils";
import { TSortDirection, TSortField, TConfigurationSortField, TGroupFieldKey, TGroupSortBy, TOptionsPanel, TTabKey, TTabs } from "./configurator-types";
import {
  Model_ConfigurationsGet,
  Model_SystemProductConfigurationItemsBulkDeletePut,
  Model_SystemProductConfigurationItemsBulkCopyPut,
  Model_SystemProductConfigurationsBulkCopyPut,
  Model_LabelAndReferenceGet,
  Model_ConfigurationBulkPut,
  Model_ConfigurationAttributeGet
} from "src/backend/openapi/v1";
import { TCustomFilter, TAttributeQueryFilter, hasAttributeFilter } from "./components/filter/filter-types";

export enum Action {
  SetActiveTab = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETACTIVETAB",

  SetSelectedConfigurations = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETSELECTEDCONFIGURATIONS",
  // Products tab selection — line items, separate from the Configurations tab's above
  SetSelectedProducts = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETSELECTEDPRODUCTS",
  // Single-id toggles, so a row's checkbox handler never has to close over the whole selection array.
  // A row that closed over it got a new callback identity on every selection change, which defeated
  // memoization and re-rendered every row in the list for one click.
  ToggleSelectedConfiguration = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:TOGGLESELECTEDCONFIGURATION",
  ToggleSelectedProduct = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:TOGGLESELECTEDPRODUCT",
  SetIsLoading = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETISLOADING",

  GetConfigurationsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONSREQUEST",
  GetConfigurationsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONSSUCCESS",
  GetConfigurationsFail = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONSFAIL",

  // The Products tab's own fetch of the same endpoint, carrying appliedFilter — separate from the
  // Configurations fetch above, which is always unfiltered
  GetProductsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETPRODUCTSREQUEST",
  GetProductsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETPRODUCTSSUCCESS",
  SetIsProductsLoading = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETISPRODUCTSLOADING",

  DeleteConfigurationRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:DELETECONFIGURATIONREQUEST",
  DeleteProjectLineItemsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:DELETEPROJECTLINEITEMSREQUEST",
  CopyConfigurationRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:COPYCONFIGURATIONREQUEST",
  CopyProjectLineItemsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:COPYPROJECTLINEITEMSREQUEST",

  GetConfigurationFiltersRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONFILTERSREQUEST",
  GetConfigurationFiltersSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONFILTERSSUCCESS",

  GetLabelsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETLABELSREQUEST",
  GetLabelsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETLABELSSUCCESS",

  GetAttributesCatalogRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETATTRIBUTESCATALOGREQUEST",
  GetAttributesCatalogSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETATTRIBUTESCATALOGSUCCESS",
  GetAttributesCatalogFail = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETATTRIBUTESCATALOGFAIL",

  PostLabelsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:POSTLABELSREQUEST",
  PostLabelsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:POSTLABELSSUCCESS",

  SetAppliedFilter = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETAPPLIEDFILTER",
  SetAppliedCustomFilters = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETAPPLIEDCUSTOMFILTERS",
  SetSearchTerm = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETSEARCHTERM",

  SetSortField = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETSORTFIELD",
  SetSortDirection = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETSORTDIRECTION",

  // Sort for the Configurations tab — separate from the Products tab's SetSortField/SetSortDirection above
  SetConfigurationSortField = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETCONFIGURATIONSORTFIELD",
  SetConfigurationSortDirection = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETCONFIGURATIONSORTDIRECTION",

  SetGroupFields = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETGROUPFIELDS",
  SetGroupSortBy = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETGROUPSORTBY",
  SetGroupSortDirection = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETGROUPSORTDIRECTION",
  TriggerExpandCollapse = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:TRIGGEREXPANDCOLLAPSE",

  SetActiveOptionsPanel = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETACTIVEOPTIONSPANEL",

  Clear = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:CLEAR"
}

type TConfiguratorState = {
  activeTab: TTabs[TTabKey]["value"];
  isLoading: boolean;
  // Always the FULL, unfiltered dataset. The Configurations tab renders it, and every incidental
  // consumer (label dialog, selection bars, the Filter panel's option lists, bulk configure,
  // breadcrumbs) resolves ids against it, so it has to stay a superset of whatever Products shows.
  configurationsState?: Model_ConfigurationsGet;
  // The Products tab's dataset — the same endpoint re-fetched with appliedFilter applied.
  // undefined means no attribute filter is in effect and that tab falls back to configurationsState.
  productsState?: Model_ConfigurationsGet;
  isProductsLoading: boolean;
  selectedConfigurations: string[];
  // systemProductConfigurationItemIds selected on the Products tab
  selectedProducts: string[];
  appliedFilter: TAttributeQueryFilter;
  appliedCustomFilters: TCustomFilter[];
  searchTerm: string;
  sortField: TSortField;
  sortDirection: TSortDirection;
  // Sort state for the Configurations tab — a separate field/direction pair from Products' sortField/sortDirection above
  configurationSortField: TConfigurationSortField;
  configurationSortDirection: TSortDirection;
  // Ordered hierarchy of group-by fields, top level first; empty array = no grouping
  groupFields: TGroupFieldKey[];
  // What the group buckets (not the items inside them) are ordered by, applied uniformly at every nesting level
  groupSortBy: TGroupSortBy;
  groupSortDirection: TSortDirection;
  // Bumped requestId so a repeated identical click (e.g. Expand All twice) still re-triggers the effect
  expandCollapseSignal: { action: "expand" | "collapse"; requestId: number } | null;
  // Which of Filter/Sort options/Group by is expanded in the shared options rail; null = all collapsed
  activeOptionsPanel: TOptionsPanel;
  isLabelsLoading: boolean;
  labels?: Model_LabelAndReferenceGet[];
  // The selectable attributes catalog (View Settings picker + Filter panel's attribute filter both
  // read from here) — owned here rather than by filterReducer since it's not Filter-panel-specific state.
  attributesCatalog: Loading<Model_ConfigurationAttributeGet[]>;
};

const initialState: TConfiguratorState = {
  activeTab: "CONFIGURATIONS",
  isLoading: false,
  configurationsState: undefined,
  productsState: undefined,
  isProductsLoading: false,
  selectedConfigurations: [],
  selectedProducts: [],
  appliedFilter: { attributes: [] },
  appliedCustomFilters: [],
  searchTerm: "",
  // Products tab defaults to "none" — no client-side sort, keeps the API's own returned order
  sortField: "none",
  sortDirection: "asc",
  // Configurations tab defaults to "none" — no client-side sort, keeps the API's own most-recent-activity order
  configurationSortField: "none",
  configurationSortDirection: "asc",
  groupFields: [],
  groupSortBy: "alphabetical",
  groupSortDirection: "asc",
  expandCollapseSignal: null,
  activeOptionsPanel: null,
  isLabelsLoading: false,
  labels: undefined,
  attributesCatalog: loadingNotStarted
};

export const configuratorActions = {
  setActiveTab: (tab: TConfiguratorState["activeTab"]) => actionCreator(Action.SetActiveTab, { tab }),

  setSelectedConfigurations: (data: TConfiguratorState["selectedConfigurations"]) => actionCreator(Action.SetSelectedConfigurations, { data }),
  setSelectedProducts: (data: TConfiguratorState["selectedProducts"]) => actionCreator(Action.SetSelectedProducts, { data }),
  toggleSelectedConfiguration: (id: string) => actionCreator(Action.ToggleSelectedConfiguration, { id }),
  toggleSelectedProduct: (id: string) => actionCreator(Action.ToggleSelectedProduct, { id }),
  setIsLoading: (data: boolean) => actionCreator(Action.SetIsLoading, { data }),
  setIsProductsLoading: (data: boolean) => actionCreator(Action.SetIsProductsLoading, { data }),

  // Always fetches unfiltered — the Configurations tab and every incidental consumer read the result
  getConfigurationsRequest: (projectId: string, filter?: TAttributeQueryFilter) => actionCreator(Action.GetConfigurationsRequest, { projectId, filter }),
  getConfigurationsSuccess: (data: Model_ConfigurationsGet) => actionCreator(Action.GetConfigurationsSuccess, { data }),

  // Fetches the same endpoint WITH appliedFilter; only the Products tab reads the result
  getProductsRequest: (projectId: string) => actionCreator(Action.GetProductsRequest, { projectId }),
  getProductsSuccess: (data: Model_ConfigurationsGet) => actionCreator(Action.GetProductsSuccess, { data }),

  getLabelsRequest: (projectId: string) => actionCreator(Action.GetLabelsRequest, { projectId }),
  getLabelsSuccess: (data: Model_LabelAndReferenceGet[]) => actionCreator(Action.GetLabelsSuccess, { data }),

  getAttributesCatalogRequest: (projectId: string) => actionCreator(Action.GetAttributesCatalogRequest, { projectId }),
  getAttributesCatalogSuccess: (data: Model_ConfigurationAttributeGet[]) => actionCreator(Action.GetAttributesCatalogSuccess, { data }),
  getAttributesCatalogFail: () => actionCreator(Action.GetAttributesCatalogFail),

  postLabelsRequest: (projectId: string, entityIds: string[], labels: string[], callback: (isSuccess: boolean, data?: Model_LabelAndReferenceGet[]) => void) =>
    actionCreator(Action.PostLabelsRequest, { projectId, entityIds, labels, callback }),

  setAppliedFilter: (filter: TAttributeQueryFilter) => actionCreator(Action.SetAppliedFilter, { filter }),
  setAppliedCustomFilters: (filters: TConfiguratorState["appliedCustomFilters"]) => actionCreator(Action.SetAppliedCustomFilters, { filters }),
  setSearchTerm: (term: string) => actionCreator(Action.SetSearchTerm, { term }),

  // Sort actions for products list
  setSortField: (field: TSortField) => actionCreator(Action.SetSortField, { field }),
  setSortDirection: (direction: TSortDirection) => actionCreator(Action.SetSortDirection, { direction }),

  // Sort actions for configurations list — mirrors setSortField/setSortDirection above but scoped to the Configurations tab's smaller field set
  setConfigurationSortField: (field: TConfigurationSortField) => actionCreator(Action.SetConfigurationSortField, { field }),
  setConfigurationSortDirection: (direction: TSortDirection) => actionCreator(Action.SetConfigurationSortDirection, { direction }),

  // Ordered hierarchy of group-by fields for products/configurations list display
  setGroupFields: (groupFields: TGroupFieldKey[]) => actionCreator(Action.SetGroupFields, { groupFields }),
  setGroupSortBy: (groupSortBy: TGroupSortBy) => actionCreator(Action.SetGroupSortBy, { groupSortBy }),
  setGroupSortDirection: (groupSortDirection: TSortDirection) => actionCreator(Action.SetGroupSortDirection, { groupSortDirection }),

  // Increments requestId itself (in the reducer) so the same action, dispatched twice in a row
  // with the same intent, still produces a new value the watching useEffect can react to
  triggerExpandCollapse: (expandAction: "expand" | "collapse") => actionCreator(Action.TriggerExpandCollapse, { expandAction }),

  // Which panel is expanded in the shared options rail (Filter / Sort options / Group by)
  setActiveOptionsPanel: (panel: TOptionsPanel) => actionCreator(Action.SetActiveOptionsPanel, { panel }),

  deleteConfigurationsRequest: (data: Model_ConfigurationBulkPut, callBack: (isSuccess: boolean) => void) => actionCreator(Action.DeleteConfigurationRequest, { data, callBack }),
  deleteProjectLineItemsRequest: (data: Model_SystemProductConfigurationItemsBulkDeletePut, callBack: (isSuccess: boolean) => void) =>
    actionCreator(Action.DeleteProjectLineItemsRequest, { data, callBack }),

  // Copies whole configuration headers (cascades to their items), not individual line items
  copyConfigurationsRequest: (blankValues: boolean, data: Model_SystemProductConfigurationsBulkCopyPut, callBack: (isSuccess: boolean) => void) =>
    actionCreator(Action.CopyConfigurationRequest, { blankValues, data, callBack }),

  // Copies individual project line items (and their children), not whole configuration headers
  copyProjectLineItemsRequest: (blankValues: boolean, data: Model_SystemProductConfigurationItemsBulkCopyPut, callBack: (isSuccess: boolean) => void) =>
    actionCreator(Action.CopyProjectLineItemsRequest, { blankValues, data, callBack }),

  clear: () => actionCreator(Action.Clear)
};

export const configuratorReducer = (state = initialState, action: ActionReturnType<typeof configuratorActions>): TConfiguratorState => {
  switch (action.type) {
    case Action.SetActiveTab:
      // Filter is Products-only (see options-rail.tsx) — switching to Configurations while the Filter
      // panel happens to be open must close it, not leave it visibly stuck open on a tab that hides its
      // own entry point for it.
      return {
        ...state,
        activeTab: action.payload.tab,
        activeOptionsPanel: action.payload.tab === "CONFIGURATIONS" && state.activeOptionsPanel === "filter" ? null : state.activeOptionsPanel
      };

    case Action.SetSelectedConfigurations:
      return { ...state, selectedConfigurations: action.payload.data };
    case Action.SetSelectedProducts:
      return { ...state, selectedProducts: action.payload.data };
    case Action.ToggleSelectedConfiguration: {
      const { id } = action.payload;
      const isSelected = state.selectedConfigurations.includes(id);
      return { ...state, selectedConfigurations: isSelected ? state.selectedConfigurations.filter(x => x !== id) : [...state.selectedConfigurations, id] };
    }
    case Action.ToggleSelectedProduct: {
      const { id } = action.payload;
      const isSelected = state.selectedProducts.includes(id);
      return { ...state, selectedProducts: isSelected ? state.selectedProducts.filter(x => x !== id) : [...state.selectedProducts, id] };
    }
    case Action.SetIsLoading:
      return { ...state, isLoading: action.payload.data };
    case Action.SetIsProductsLoading:
      return { ...state, isProductsLoading: action.payload.data };

    case Action.GetConfigurationsRequest:
      return action.payload.filter ? { ...state, appliedFilter: action.payload.filter } : state;
    case Action.GetConfigurationsSuccess:
      return { ...state, configurationsState: action.payload.data };

    case Action.GetProductsSuccess:
      return { ...state, productsState: action.payload.data };

    case Action.GetLabelsRequest:
      return { ...state, isLabelsLoading: state.labels ? false : true };
    case Action.GetLabelsSuccess:
      return { ...state, isLabelsLoading: false, labels: action.payload.data };

    case Action.GetAttributesCatalogRequest:
      return { ...state, attributesCatalog: loadingIsLoading };
    case Action.GetAttributesCatalogSuccess:
      return { ...state, attributesCatalog: loadingFinished(action.payload.data) };
    case Action.GetAttributesCatalogFail:
      return { ...state, attributesCatalog: loadingFailed };

    case Action.SetAppliedFilter:
      // Single choke point for dropping the filtered dataset: every path that clears the attribute
      // filter (Filter panel Reset, removing the last applied-filter chip) goes through here, so the
      // Products tab falls straight back to the unfiltered configurationsState rather than keeping
      // the last filtered result on screen.
      return { ...state, appliedFilter: action.payload.filter, productsState: hasAttributeFilter(action.payload.filter) ? state.productsState : undefined };
    case Action.SetAppliedCustomFilters:
      return { ...state, appliedCustomFilters: action.payload.filters };
    case Action.SetSearchTerm:
      return { ...state, searchTerm: action.payload.term };

    // Update sort field for products list display
    case Action.SetSortField:
      return { ...state, sortField: action.payload.field };
    // Update sort direction (asc/desc) for products list display
    case Action.SetSortDirection:
      return { ...state, sortDirection: action.payload.direction };

    // Update sort field for configurations list display
    case Action.SetConfigurationSortField:
      return { ...state, configurationSortField: action.payload.field };
    // Update sort direction (asc/desc) for configurations list display
    case Action.SetConfigurationSortDirection:
      return { ...state, configurationSortDirection: action.payload.direction };

    // Update the group-by hierarchy for products/configurations list display
    case Action.SetGroupFields:
      return { ...state, groupFields: action.payload.groupFields };
    case Action.SetGroupSortBy:
      return { ...state, groupSortBy: action.payload.groupSortBy };
    case Action.SetGroupSortDirection:
      return { ...state, groupSortDirection: action.payload.groupSortDirection };

    case Action.TriggerExpandCollapse:
      return { ...state, expandCollapseSignal: { action: action.payload.expandAction, requestId: (state.expandCollapseSignal?.requestId ?? 0) + 1 } };

    // Update which panel is expanded in the shared options rail
    case Action.SetActiveOptionsPanel:
      return { ...state, activeOptionsPanel: action.payload.panel };

    case Action.Clear:
      return initialState;
    default:
      return state;
  }
};
